/**
 * Visionboard Firebase Service
 * 
 * Manages visionboard images for goals.
 * Supports both file uploads and external URLs.
 */

import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage } from '../../../../firebase';
import { VisionboardImage, VISIONBOARD_MAX_IMAGES } from '../types';

const COLLECTION_NAME = 'visionboardImages';

/**
 * Get all visionboard images for a goal
 */
export async function getVisionboardImages(
  ownerId: string,
  goalId: string
): Promise<VisionboardImage[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId),
    orderBy('order', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as VisionboardImage[];
}

/**
 * Add image from file upload
 */
export async function addVisionboardImageFromFile(
  ownerId: string,
  goalId: string,
  file: Blob,
  caption?: string
): Promise<string> {
  // Check limit
  const existing = await getVisionboardImages(ownerId, goalId);
  if (existing.length >= VISIONBOARD_MAX_IMAGES) {
    throw new Error(`Maximum ${VISIONBOARD_MAX_IMAGES} afbeeldingen bereikt`);
  }

  // Upload to Firebase Storage
  const fileName = `visionboard/${ownerId}/${goalId}/${Date.now()}.jpg`;
  const storageRef = ref(storage, fileName);
  await uploadBytes(storageRef, file);
  const imageUrl = await getDownloadURL(storageRef);

  // Calculate next order
  const nextOrder = existing.length > 0 
    ? Math.max(...existing.map(img => img.order)) + 1 
    : 0;

  // Save to Firestore
  const imageData: Omit<VisionboardImage, 'id'> = {
    goalId,
    ownerId,
    imageUrl,
    caption: caption || '',
    order: nextOrder,
    sourceType: 'upload',
    createdAt: Date.now(),
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), imageData);
  return docRef.id;
}

/**
 * Add image from external URL
 */
export async function addVisionboardImageFromUrl(
  ownerId: string,
  goalId: string,
  imageUrl: string,
  caption?: string
): Promise<string> {
  // Check limit
  const existing = await getVisionboardImages(ownerId, goalId);
  if (existing.length >= VISIONBOARD_MAX_IMAGES) {
    throw new Error(`Maximum ${VISIONBOARD_MAX_IMAGES} afbeeldingen bereikt`);
  }

  // Calculate next order
  const nextOrder = existing.length > 0 
    ? Math.max(...existing.map(img => img.order)) + 1 
    : 0;

  // Save to Firestore
  const imageData: Omit<VisionboardImage, 'id'> = {
    goalId,
    ownerId,
    imageUrl,
    caption: caption || '',
    order: nextOrder,
    sourceType: 'url',
    createdAt: Date.now(),
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), imageData);
  return docRef.id;
}

/**
 * Update image caption
 */
export async function updateVisionboardCaption(
  imageId: string,
  caption: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, imageId);
  await updateDoc(docRef, { caption });
}

/**
 * Update image order (after drag & drop)
 */
export async function updateVisionboardOrder(
  images: { id: string; order: number }[]
): Promise<void> {
  const batch = writeBatch(db);
  
  images.forEach(({ id, order }) => {
    const docRef = doc(db, COLLECTION_NAME, id);
    batch.update(docRef, { order });
  });

  await batch.commit();
}

/**
 * Delete visionboard image
 */
export async function deleteVisionboardImage(
  image: VisionboardImage
): Promise<void> {
  // Delete from Storage if uploaded
  if (image.sourceType === 'upload' && image.imageUrl.includes('firebase')) {
    try {
      const storageRef = ref(storage, image.imageUrl);
      await deleteObject(storageRef);
    } catch (err) {
      // Storage file may not exist, continue with Firestore delete
      console.warn('Could not delete storage file:', err);
    }
  }

  // Delete from Firestore
  const docRef = doc(db, COLLECTION_NAME, image.id);
  await deleteDoc(docRef);
}

/**
 * Get count of images for a goal
 */
export async function getVisionboardCount(
  ownerId: string,
  goalId: string
): Promise<number> {
  const images = await getVisionboardImages(ownerId, goalId);
  return images.length;
}
